import { useCallback, useEffect, useState } from "react";

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

// The browser fires this once, often before React mounts, so catch it at module load.
let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e as InstallEvent; listeners.forEach((l) => l()); });
  window.addEventListener("appinstalled", () => { deferred = null; listeners.forEach((l) => l()); });
}

/** "Install app": available only when the browser offers it (Chrome/Edge desktop and Android). */
export function useInstallPrompt() {
  const [available, setAvailable] = useState(() => !!deferred);
  useEffect(() => {
    const update = () => setAvailable(!!deferred);
    listeners.add(update);
    update();
    return () => { listeners.delete(update); };
  }, []);
  const prompt = useCallback(async () => {
    if (!deferred) return;
    const e = deferred;
    deferred = null;
    await e.prompt();
    await e.userChoice.catch(() => undefined);
    listeners.forEach((l) => l());
  }, []);
  return { available, prompt };
}
